import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { MagnifyingGlass, Plus, Download, Check, Ticket, ArrowLeft, ArrowRight, FileXls, Upload } from "@phosphor-icons/react";
import { toast } from "sonner";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function VouchersPage() {
  const [vouchers, setVouchers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(1);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [stats, setStats] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [newVoucher, setNewVoucher] = useState({ code: "", brand: "", value: "", validity: "" });
  const [bulkCodes, setBulkCodes] = useState("");
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchVouchers = useCallback(async () => {
    try {
      setIsLoading(true);
      const params = new URLSearchParams();
      params.append("page", page);
      params.append("limit", 20);
      if (search) params.append("search", search);
      if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
      const [vouchersRes, statsRes] = await Promise.all([
        axios.get(`${API}/vouchers?${params.toString()}`),
        axios.get(`${API}/vouchers/stats`)
      ]);
      setVouchers(vouchersRes.data.vouchers);
      setTotal(vouchersRes.data.total);
      setPages(vouchersRes.data.pages);
      setStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load vouchers");
    } finally {
      setIsLoading(false);
    }
  }, [page, search, statusFilter]);

  useEffect(() => { fetchVouchers(); }, [fetchVouchers]);

  const handleSearch = (e) => { e.preventDefault(); setPage(1); fetchVouchers(); };

  const handleAddVoucher = async (e) => {
    e.preventDefault();
    try {
      const payload = { code: newVoucher.code };
      if (newVoucher.brand) payload.brand = newVoucher.brand;
      if (newVoucher.value) payload.value = parseFloat(newVoucher.value);
      if (newVoucher.validity) payload.validity = newVoucher.validity;
      await axios.post(`${API}/vouchers`, payload);
      toast.success("Voucher added");
      setShowAddDialog(false);
      setNewVoucher({ code: "", brand: "", value: "", validity: "" });
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to add voucher");
    }
  };

  const handleBulkAdd = async (e) => {
    e.preventDefault();
    const codes = bulkCodes.split("\n").map(c => c.trim()).filter(c => c);
    if (codes.length === 0) { toast.error("No valid codes"); return; }
    try {
      const payload = codes.map(code => ({ code }));
      const res = await axios.post(`${API}/vouchers/bulk`, payload);
      const msg = `Added ${res.data.created} vouchers`;
      if (res.data.duplicates > 0) {
        toast.warning(`${msg}. ${res.data.duplicates} duplicates skipped.`);
      } else {
        toast.success(msg);
      }
      setShowAddDialog(false);
      setBulkCodes("");
      fetchVouchers();
    } catch (error) {
      toast.error("Failed to add bulk vouchers");
    }
  };

  const handleExcelUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.match(/\.(xlsx|xls)$/i)) {
      toast.error("Please upload an Excel file (.xlsx or .xls)");
      return;
    }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await axios.post(`${API}/vouchers/upload-excel`, formData, { headers: { "Content-Type": "multipart/form-data" } });
      const msg = `Created ${res.data.created} vouchers from Excel`;
      if (res.data.duplicates > 0) {
        toast.warning(`${msg}. ${res.data.duplicates} duplicates skipped.`);
      } else {
        toast.success(msg);
      }
      setShowAddDialog(false);
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to process Excel file");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = "";
    }
  };

  const handleRedeem = async (voucherId) => {
    try {
      await axios.put(`${API}/vouchers/${voucherId}/redeem`);
      toast.success("Voucher redeemed");
      fetchVouchers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed");
    }
  };

  const seedVouchers = async () => {
    try {
      const res = await axios.post(`${API}/seed/vouchers`);
      toast.success(res.data.message);
      fetchVouchers();
    } catch (error) {
      toast.error("Failed");
    }
  };

  const exportVouchers = () => {
    const params = new URLSearchParams();
    if (statusFilter && statusFilter !== "all") params.append("status", statusFilter);
    window.open(`${API}/reports/vouchers/export?${params.toString()}`, "_blank");
    toast.success("Export started");
  };

  const getStatusBadge = (status) => {
    const styles = { available: "status-available", assigned: "status-assigned", redeemed: "status-redeemed", expired: "status-expired" };
    return styles[status] || styles.available;
  };

  return (
    <div className="space-y-6" data-testid="vouchers-page">
      <div className="flex flex-col sm:flex-row gap-4 justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold text-[#0A0A0A]">Vouchers</h2>
          <p className="text-[#64748B] text-sm mt-1">Manage voucher codes for the loyalty program</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={seedVouchers} className="gap-2" data-testid="seed-vouchers-btn"><Ticket size={18} />Seed Test</Button>
          <Button variant="outline" onClick={exportVouchers} className="gap-2" data-testid="export-vouchers-btn"><Download size={18} />Export</Button>
          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild><Button className="bg-[#002FA7] hover:bg-[#001F6E] gap-2" data-testid="add-voucher-btn"><Plus size={18} />Add</Button></DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader><DialogTitle className="font-heading">Add Vouchers</DialogTitle></DialogHeader>
              <Tabs defaultValue="single" className="mt-4">
                <TabsList className="grid w-full grid-cols-3">
                  <TabsTrigger value="single">Single</TabsTrigger>
                  <TabsTrigger value="bulk">Bulk Text</TabsTrigger>
                  <TabsTrigger value="excel">Excel Import</TabsTrigger>
                </TabsList>

                <TabsContent value="single" className="mt-4">
                  <form onSubmit={handleAddVoucher} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Voucher Code *</Label>
                      <Input placeholder="e.g., DOMINOS500" value={newVoucher.code} onChange={(e) => setNewVoucher({ ...newVoucher, code: e.target.value })} required data-testid="new-voucher-code" />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Brand</Label>
                        <Input placeholder="e.g., Dominos" value={newVoucher.brand} onChange={(e) => setNewVoucher({ ...newVoucher, brand: e.target.value })} data-testid="new-voucher-brand" />
                      </div>
                      <div className="space-y-2">
                        <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Value</Label>
                        <Input type="number" placeholder="e.g., 500" value={newVoucher.value} onChange={(e) => setNewVoucher({ ...newVoucher, value: e.target.value })} data-testid="new-voucher-value" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Validity</Label>
                      <Input type="date" value={newVoucher.validity} onChange={(e) => setNewVoucher({ ...newVoucher, validity: e.target.value })} data-testid="new-voucher-validity" />
                    </div>
                    <Button type="submit" className="w-full bg-[#002FA7] hover:bg-[#001F6E]" data-testid="submit-single-voucher">Add Voucher</Button>
                  </form>
                </TabsContent>

                <TabsContent value="bulk" className="mt-4">
                  <form onSubmit={handleBulkAdd} className="space-y-4">
                    <div className="space-y-2">
                      <Label className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Paste codes (one per line)</Label>
                      <textarea className="w-full h-40 px-3 py-2 border border-[#E2E8F0] rounded-md text-sm font-mono resize-none focus:outline-none focus:ring-2 focus:ring-[#002FA7]" placeholder={"CODE001\nCODE002\nCODE003"} value={bulkCodes} onChange={(e) => setBulkCodes(e.target.value)} data-testid="bulk-voucher-codes" />
                    </div>
                    <p className="text-xs text-[#94A3B8]">Duplicate codes will be automatically skipped</p>
                    <Button type="submit" className="w-full bg-[#002FA7] hover:bg-[#001F6E]" data-testid="submit-bulk-vouchers">Add Bulk</Button>
                  </form>
                </TabsContent>

                <TabsContent value="excel" className="mt-4">
                  <div className="space-y-4">
                    <div className="p-4 bg-[#F8FAFC] rounded-lg border border-[#E2E8F0]">
                      <p className="font-medium text-sm mb-2">Excel Format:</p>
                      <div className="overflow-x-auto">
                        <table className="text-xs font-mono border-collapse w-full">
                          <thead><tr className="bg-[#E2E8F0]">
                            <th className="border px-2 py-1 text-left">Voucher Code</th>
                            <th className="border px-2 py-1 text-left">Brand</th>
                            <th className="border px-2 py-1 text-left">Value</th>
                            <th className="border px-2 py-1 text-left">Validity</th>
                          </tr></thead>
                          <tbody>
                            <tr><td className="border px-2 py-1">DOM500</td><td className="border px-2 py-1">Dominos</td><td className="border px-2 py-1">500</td><td className="border px-2 py-1">2026-12-31</td></tr>
                            <tr><td className="border px-2 py-1">LEV200</td><td className="border px-2 py-1">Levis</td><td className="border px-2 py-1">200</td><td className="border px-2 py-1">2026-06-30</td></tr>
                          </tbody>
                        </table>
                      </div>
                    </div>
                    <div>
                      <input ref={fileInputRef} type="file" accept=".xlsx,.xls" onChange={handleExcelUpload} className="hidden" id="excel-upload" data-testid="excel-file-input" />
                      <Button asChild variant="outline" className="w-full gap-2 h-20 border-dashed border-2" disabled={uploading}>
                        <label htmlFor="excel-upload" className="cursor-pointer flex flex-col items-center">
                          <FileXls size={28} className="text-[#002FA7] mb-1" />
                          <span className="text-sm">{uploading ? "Processing..." : "Click to upload Excel file"}</span>
                        </label>
                      </Button>
                    </div>
                    <p className="text-xs text-[#94A3B8]">Duplicate voucher codes will be skipped automatically</p>
                  </div>
                </TabsContent>
              </Tabs>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {stats && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Card className="border border-[#E2E8F0]" data-testid="voucher-stat-total"><CardContent className="p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Total</p><p className="font-heading text-2xl font-bold text-[#0A0A0A]">{stats.total}</p></CardContent></Card>
          <Card className="border border-[#E2E8F0]" data-testid="voucher-stat-available"><CardContent className="p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Available</p><p className="font-heading text-2xl font-bold text-[#10B981]">{stats.available}</p></CardContent></Card>
          <Card className="border border-[#E2E8F0]" data-testid="voucher-stat-assigned"><CardContent className="p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Assigned</p><p className="font-heading text-2xl font-bold text-[#002FA7]">{stats.assigned}</p></CardContent></Card>
          <Card className="border border-[#E2E8F0]" data-testid="voucher-stat-redeemed"><CardContent className="p-4"><p className="text-xs uppercase tracking-[0.2em] text-[#64748B]">Redeemed</p><p className="font-heading text-2xl font-bold text-[#8B5CF6]">{stats.redeemed}</p></CardContent></Card>
        </div>
      )}

      <Card className="border border-[#E2E8F0]"><CardContent className="p-4"><div className="flex flex-col sm:flex-row gap-4">
        <form onSubmit={handleSearch} className="flex-1 flex gap-2"><div className="relative flex-1"><MagnifyingGlass size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#64748B]" /><Input placeholder="Search by code or phone..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" data-testid="voucher-search-input" /></div><Button type="submit" variant="secondary" data-testid="voucher-search-btn">Search</Button></form>
        <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}><SelectTrigger className="w-40" data-testid="voucher-status-filter"><SelectValue placeholder="All statuses" /></SelectTrigger><SelectContent><SelectItem value="all">All</SelectItem><SelectItem value="available">Available</SelectItem><SelectItem value="assigned">Assigned</SelectItem><SelectItem value="redeemed">Redeemed</SelectItem></SelectContent></Select>
      </div></CardContent></Card>

      <Card className="border border-[#E2E8F0]">
        <CardHeader className="border-b border-[#E2E8F0] pb-4"><CardTitle className="font-heading text-lg flex items-center justify-between"><span>Voucher Codes</span><span className="text-sm font-normal text-[#64748B]">{total} total</span></CardTitle></CardHeader>
        <CardContent className="p-0">
          {isLoading ? <div className="p-8 text-center text-[#64748B]">Loading...</div> : vouchers.length === 0 ? <div className="p-8 text-center text-[#64748B]">No vouchers found. Click "Seed Test" to add test data.</div> : (<>
            <div className="overflow-x-auto"><Table className="data-table"><TableHeader><TableRow className="bg-[#F8FAFC]">
              <TableHead>Code</TableHead>
              <TableHead>Brand</TableHead>
              <TableHead>Value</TableHead>
              <TableHead>Validity</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Assigned To</TableHead>
              <TableHead>Assigned At</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow></TableHeader><TableBody>
              {vouchers.map((v) => (
                <TableRow key={v.id} className="table-row-hover" data-testid={`voucher-row-${v.id}`}>
                  <TableCell className="font-mono text-sm font-medium">{v.code}</TableCell>
                  <TableCell>{v.brand ? <Badge className="bg-[#FEF3C7] text-[#92400E]">{v.brand}</Badge> : <span className="text-[#94A3B8]">-</span>}</TableCell>
                  <TableCell className="font-mono text-sm">{v.value ? `Rs.${v.value}` : "-"}</TableCell>
                  <TableCell className="font-mono text-sm text-[#64748B]">{v.validity || "-"}</TableCell>
                  <TableCell><Badge className={`${getStatusBadge(v.status)} font-medium`}>{v.status}</Badge></TableCell>
                  <TableCell className="font-mono text-sm">{v.assigned_phone || "-"}</TableCell>
                  <TableCell className="font-mono text-sm text-[#64748B]">{v.assigned_at ? format(new Date(v.assigned_at), "MMM dd, HH:mm") : "-"}</TableCell>
                  <TableCell className="text-right">{v.status === "assigned" && (<Button variant="ghost" size="sm" onClick={() => handleRedeem(v.id)} className="gap-1 text-[#10B981]" data-testid={`redeem-voucher-${v.id}`}><Check size={16} weight="bold" />Redeem</Button>)}</TableCell>
                </TableRow>
              ))}
            </TableBody></Table></div>
            <div className="flex items-center justify-between px-4 py-3 border-t border-[#E2E8F0]"><p className="text-sm text-[#64748B]">Page {page} of {pages}</p><div className="flex gap-2"><Button variant="outline" size="sm" onClick={() => setPage(Math.max(1, page - 1))} disabled={page === 1} data-testid="voucher-prev-page"><ArrowLeft size={16} /></Button><Button variant="outline" size="sm" onClick={() => setPage(Math.min(pages, page + 1))} disabled={page === pages} data-testid="voucher-next-page"><ArrowRight size={16} /></Button></div></div>
          </>)}
        </CardContent>
      </Card>
    </div>
  );
}
